import React, { useCallback, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Tooltip from "@mui/material/Tooltip";

import { retailSalesConstants } from "../../../../../_redux/_constants/retailSales.constants";
import {
     normaliseWeek,
     WEEK_PROBLEM_MESSAGE,
} from "../../retailSalesWeek";

// Kim's weekly winner list holds human values, so the dealer code and role name are matched to our
// records on upload. Everything stays editable afterwards - the sheet is a starting point, not the
// final word.
const COLUMN_ALIASES = {
     dealerCode: ["dealer code", "dealercode"],
     dealerName: ["dealer name", "dealername"],
     role: ["role"],
     firstName: ["first name", "firstname"],
     lastName: ["last name", "lastname"],
     email: ["email", "e-mail"],
     reward: ["reward", "points"],
     week: ["week"],
     area: ["area"],
};

// The week header on the real sheet is written "Week (1,2,3 etc)", so a header is a match when it
// starts with the alias rather than equalling it. Longer aliases are tried first so "dealer name"
// can never be claimed by a shorter one.
const headerMatches = (header, field) => {
     const key = header.trim().toLowerCase();

     return COLUMN_ALIASES[field].some(
          (alias) => key === alias || key.startsWith(alias),
     );
};

const readCell = (sheetRow, field) => {
     const keys = Object.keys(sheetRow);

     const match =
          keys.find((key) =>
               COLUMN_ALIASES[field].includes(key.trim().toLowerCase()),
          ) || keys.find((key) => headerMatches(key, field));

     const value = match ? sheetRow[match] : "";

     return value === null || value === undefined ? "" : String(value).trim();
};


// Shown before a file is picked, so an administrator knows what pressing add will do before it
// happens rather than after. These must stay in step with the rules the backend applies.
const UPLOAD_RULES = [
     "Each row is added on its own, so one bad row never stops the rest.",
     "An email we do not know gets a login and an invitation naming the reward and the week. An email we already know keeps its login and is told it has won again.",
     "A repeat winner at the same dealer and role is topped up, never added twice.",
     "The reward is credited straight to the winner's points, 100 points to the pound, and the week names it on their statement.",
     "A dealer that produces a winner is flagged as a Retail Car Sales dealer. Its other flags are left alone.",
];

const rowProblem = (row) => {
     const problems = [];

     if (!row.firstName) {
          problems.push("First name missing");
     }

     if (!row.lastName) {
          problems.push("Last name missing");
     }

     if (!row.email) {
          problems.push("Email missing");
     }

     if (!row.dealer) {
          problems.push("Dealer not set");
     }

     if (!row.jobRole) {
          problems.push("Role not set");
     }

     if (!row.points || row.points <= 0) {
          problems.push("Points must be more than zero");
     }

     if (!normaliseWeek(row.week)) {
          problems.push(WEEK_PROBLEM_MESSAGE);
     }

     return problems.join("; ");
};

export default function AddWinnersImport(props) {
     const { dealerOptions, jobRoleOptions, jobTitleOptions, isSubmitting, onSubmit } =
          props;

     const [rows, setRows] = useState([]);
     const [fileName, setFileName] = useState("");
     const [readError, setReadError] = useState("");

     const buildRow = (sheetRow) => {
          const dealerCode = readCell(sheetRow, "dealerCode");
          const roleName = readCell(sheetRow, "role");
          const reward = readCell(sheetRow, "reward");

          const dealer = dealerOptions.find(
               (item) =>
                    (item.dealerCode || "").trim().toLowerCase() ===
                    dealerCode.toLowerCase(),
          );

          // The sheet has one Role column. It can name either a system role (which decides the
          // portal view) or a job title (a label). Match it against both lists and take whichever
          // it turns out to be; if it is neither, both are left empty for the user to set.
          const matches = (item) =>
               item.name.trim().toLowerCase() === roleName.toLowerCase();

          const jobRole = jobRoleOptions.find(matches);
          const jobTitle = jobTitleOptions.find(matches);

          return {
               firstName: readCell(sheetRow, "firstName"),
               lastName: readCell(sheetRow, "lastName"),
               email: readCell(sheetRow, "email"),
               points:
                    reward === ""
                         ? retailSalesConstants.RETAIL_SALES_DEFAULT_REWARD_POINTS
                         : Number(String(reward).replace(/[^0-9.]/g, "")) ||
                           retailSalesConstants.RETAIL_SALES_DEFAULT_REWARD_POINTS,
               week: readCell(sheetRow, "week"),
               dealer: dealer || null,
               jobRole: jobRole || null,
               jobTitle: jobTitle || null,
               // The area the sheet puts the dealer in. Sent as written and resolved by the backend,
               // which applies it only to a dealer that has no area yet.
               area: readCell(sheetRow, "area"),
               // Kept so an unmatched code or role can be shown back to the user.
               sheetDealerCode: dealerCode,
               sheetRole: roleName,
          };
     };

     const readFile = (event) => {
          const file = event.target.files && event.target.files[0];

          if (!file) {
               return;
          }

          setFileName(file.name);
          setReadError("");

          const reader = new FileReader();

          reader.onload = (loadEvent) => {
               try {
                    const workbook = XLSX.read(loadEvent.target.result, {
                         type: "array",
                    });

                    const sheet = workbook.Sheets[workbook.SheetNames[0]];
                    const sheetRows = XLSX.utils.sheet_to_json(sheet, {
                         defval: "",
                    });

                    setRows(sheetRows.map(buildRow));
               } catch (error) {
                    setRows([]);
                    setReadError(
                         "That file could not be read as a spreadsheet.",
                    );
               }
          };

          reader.readAsArrayBuffer(file);
     };

     // Kept stable so the cell components below never have to be rebuilt.
     const setRowField = useCallback((rowIndex, field, value) => {
          setRows((previous) =>
               previous.map((row, index) =>
                    index === rowIndex ? { ...row, [field]: value } : row,
               ),
          );
     }, []);

     // A winner can be missed off the weekly sheet, or come in after it was sent. Adding a blank row
     // appends to whatever is already in the grid, so it works before an import as well as after one.
     const addRow = () => {
          setRows((previous) => [
               ...previous,
               {
                    firstName: "",
                    lastName: "",
                    email: "",
                    points: retailSalesConstants.RETAIL_SALES_DEFAULT_REWARD_POINTS,
                    // A row added by hand almost always belongs to the same week as the sheet it is
                    // being added to, so the last row's week is carried over as the starting value.
                    week: previous.length
                         ? previous[previous.length - 1].week
                         : "",
                    dealer: null,
                    jobRole: null,
                    jobTitle: null,
                    area: "",
                    sheetDealerCode: "",
                    sheetRole: "",
               },
          ]);
     };

     const removeRow = useCallback((rowIndex) => {
          setRows((previous) =>
               previous.filter((row, index) => index !== rowIndex),
          );
     }, []);

     const gridRows = useMemo(
          () =>
               rows.map((row, index) => ({
                    ...row,
                    rowIndex: index,
                    rowNumber: index + 1,
                    problem: rowProblem(row),
               })),
          [rows],
     );

     const problemCount = gridRows.filter((row) => row.problem).length;

     // Each cell component is built once. Rebuilding them on every render would make React treat
     // each keystroke as a new component, throwing away the input and the caret with it.
     const cells = useMemo(() => {
          const textCell = (field) => (cellProps) => (
               <td>
                    <Input
                         style={{ width: "100%" }}
                         value={cellProps.dataItem[field]}
                         onChange={(event) =>
                              setRowField(
                                   cellProps.dataItem.rowIndex,
                                   field,
                                   event.value,
                              )
                         }
                    />
               </td>
          );

          return {
               firstName: textCell("firstName"),
               lastName: textCell("lastName"),
               email: textCell("email"),
               week: textCell("week"),
               area: textCell("area"),

               dealer: (cellProps) => (
                    <td>
                         <DropDownList
                              style={{ width: "100%" }}
                              data={dealerOptions}
                              textField="label"
                              dataItemKey="id"
                              value={cellProps.dataItem.dealer}
                              onChange={(event) =>
                                   setRowField(
                                        cellProps.dataItem.rowIndex,
                                        "dealer",
                                        event.value,
                                   )
                              }
                         />
                    </td>
               ),

               jobRole: (cellProps) => (
                    <td>
                         <DropDownList
                              style={{ width: "100%" }}
                              data={jobRoleOptions}
                              textField="name"
                              dataItemKey="id"
                              value={cellProps.dataItem.jobRole}
                              onChange={(event) =>
                                   setRowField(
                                        cellProps.dataItem.rowIndex,
                                        "jobRole",
                                        event.value,
                                   )
                              }
                         />
                    </td>
               ),

               jobTitle: (cellProps) => (
                    <td>
                         <DropDownList
                              style={{ width: "100%" }}
                              data={jobTitleOptions}
                              textField="name"
                              dataItemKey="id"
                              value={cellProps.dataItem.jobTitle}
                              onChange={(event) =>
                                   setRowField(
                                        cellProps.dataItem.rowIndex,
                                        "jobTitle",
                                        event.value,
                                   )
                              }
                         />
                    </td>
               ),

               points: (cellProps) => (
                    <td>
                         <NumericTextBox
                              style={{ width: "100%" }}
                              min={0}
                              step={100}
                              value={cellProps.dataItem.points}
                              onChange={(event) =>
                                   setRowField(
                                        cellProps.dataItem.rowIndex,
                                        "points",
                                        event.value,
                                   )
                              }
                         />
                    </td>
               ),

               remove: (cellProps) => (
                    <td>
                         <Tooltip title="Remove row" placement="top" arrow>
                              <IconButton
                                   aria-label="Remove row"
                                   onClick={() =>
                                        removeRow(cellProps.dataItem.rowIndex)
                                   }
                              >
                                   <DeleteIcon color="error" />
                              </IconButton>
                         </Tooltip>
                    </td>
               ),
          };
     }, [dealerOptions, jobRoleOptions, jobTitleOptions, setRowField, removeRow]);

     const rowRender = (row, gridRowProps) => {
          if (!gridRowProps.dataItem.problem) {
               return row;
          }

          return React.cloneElement(row, {
               ...row.props,
               style: { ...row.props.style, backgroundColor: "#fdecea" },
          });
     };

     const submit = () => {
          onSubmit(
               rows.map((row) => ({
                    dealershipId: row.dealer.id,
                    jobRoleId: row.jobRole.id,
                    email: row.email.trim(),
                    firstName: row.firstName.trim(),
                    lastName: row.lastName.trim(),
                    rewardValueGbp:
                         Number(row.points) /
                         retailSalesConstants.RETAIL_SALES_POINTS_PER_GBP,
                    week: normaliseWeek(row.week),
                    jobTitle: row.jobTitle ? row.jobTitle.name : undefined,
                    retailArea: row.area ? String(row.area).trim() : undefined,
               })),
          );
     };

     return (
          <div>
               <p>
                    Upload the weekly winner list, check every row and correct
                    anything that is wrong, then add them all in one go.
                    Expected columns: Week, Area, Dealer Code, Dealer Name,
                    Role, First Name, Last Name, email, Reward. The week can be
                    written as 1, WK1 or Week 1.
               </p>

               {!fileName && (
                    <Box component="section" sx={{ mb: 3 }}>
                         <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                              What this upload does
                         </Typography>

                         <Box component="ul" sx={{ m: 0, pl: 3 }}>
                              {UPLOAD_RULES.map((rule) => (
                                   <Typography component="li" variant="body2" key={rule} sx={{ mb: 0.5 }}>
                                        {rule}
                                   </Typography>
                              ))}
                         </Box>
                    </Box>
               )}

               <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={readFile}
               />

               {fileName && (
                    <p style={{ marginTop: "8px" }}>
                         {fileName} - {rows.length} row
                         {rows.length === 1 ? "" : "s"}
                         {problemCount > 0
                              ? `, ${problemCount} with an issue to fix`
                              : ", all ready"}
                    </p>
               )}

               {readError && <p style={{ color: "#c62828" }}>{readError}</p>}

               {rows.length > 0 && (
                    <>
                         <Grid
                              data={gridRows}
                              className="sba-area-data-grid"
                              rowRender={rowRender}
                         >
                              <GridColumn
                                   field="rowNumber"
                                   title="Row"
                                   width="70px"
                              />
                              <GridColumn
                                   field="firstName"
                                   title="First Name"
                                   width="160px"
                                   cells={{ data: cells.firstName }}
                              />
                              <GridColumn
                                   field="lastName"
                                   title="Last Name"
                                   width="160px"
                                   cells={{ data: cells.lastName }}
                              />
                              <GridColumn
                                   field="email"
                                   title="Email"
                                   width="260px"
                                   cells={{ data: cells.email }}
                              />
                              <GridColumn
                                   field="dealer"
                                   title="Dealer"
                                   width="280px"
                                   cells={{ data: cells.dealer }}
                              />
                              <GridColumn
                                   field="jobRole"
                                   title="Role"
                                   width="190px"
                                   cells={{ data: cells.jobRole }}
                              />
                              <GridColumn
                                   field="jobTitle"
                                   title="Job Title"
                                   width="190px"
                                   cells={{ data: cells.jobTitle }}
                              />
                              <GridColumn
                                   field="area"
                                   title="Retail Area"
                                   width="120px"
                                   cells={{ data: cells.area }}
                              />
                              <GridColumn
                                   field="week"
                                   title="Week"
                                   width="110px"
                                   cells={{ data: cells.week }}
                              />
                              <GridColumn
                                   field="points"
                                   title="Points"
                                   width="180px"
                                   cells={{ data: cells.points }}
                              />
                              <GridColumn
                                   field="problem"
                                   title="Issue"
                                   width="240px"
                              />
                              <GridColumn
                                   title=" "
                                   width="80px"
                                   cells={{ data: cells.remove }}
                              />
                         </Grid>

                         {/* Sits under the right-hand end of the table, where the row it adds
                             will appear. */}
                         <div
                              style={{
                                   display: "flex",
                                   justifyContent: "flex-end",
                                   marginTop: "8px",
                              }}
                         >
                              <Tooltip title="Add a row" placement="left" arrow>
                                   <IconButton
                                        aria-label="Add a row"
                                        onClick={addRow}
                                        sx={{
                                             backgroundColor: "#1976d2",
                                             color: "#fff",
                                             "&:hover": {
                                                  backgroundColor: "#1565c0",
                                             },
                                        }}
                                   >
                                        <AddIcon />
                                   </IconButton>
                              </Tooltip>
                         </div>

                         <div style={{ marginTop: "16px" }}>
                              <Button
                                   type="button"
                                   themeColor="primary"
                                   disabled={problemCount > 0 || isSubmitting}
                                   onClick={submit}
                              >
                                   {isSubmitting ? "Adding..." : `Add ${rows.length} winner${rows.length === 1 ? "" : "s"}`}
                              </Button>
                              {problemCount > 0 && (
                                   <span style={{ marginLeft: "12px" }}>
                                        Fix the highlighted rows first, or
                                        remove them.
                                   </span>
                              )}
                         </div>
                    </>
               )}
          </div>
     );
}
